import React from 'react';
import {
  Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { IndicatorUnit, ResultRow, ThresholdStatus } from '../../../services/indicatorRegistryApi';

const STATUS_COLOR: Record<ThresholdStatus, 'success' | 'warning' | 'error'> = {
  good: 'success',
  watch: 'warning',
  poor: 'error',
};

export function formatValue(value: ResultRow['value'], unit: IndicatorUnit, decimals: number): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (unit === 'count') return n.toLocaleString();
  const text = n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return unit === 'percent' ? `${text}%` : text;
}

const num = (v: ResultRow['numerator'] | null) => (v === null ? '—' : Number(v).toLocaleString());

interface Props {
  rows: ResultRow[];
  unit: IndicatorUnit;
  decimals: number;
  hasDenominator: boolean;
  /** Label for the breakdown column; omit when the rows are totals only. */
  breakdownLabel?: string;
  maxHeight?: number;
}

const ResultsTable: React.FC<Props> = ({ rows, unit, decimals, hasDenominator, breakdownLabel, maxHeight = 420 }) => {
  if (!rows.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No values for this selection.</Typography>
      </Box>
    );
  }
  return (
    <TableContainer sx={{ maxHeight, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Area</TableCell>
            {breakdownLabel && <TableCell>{breakdownLabel}</TableCell>}
            <TableCell align="right">Value</TableCell>
            <TableCell align="right">Numerator</TableCell>
            {hasDenominator && <TableCell align="right">Denominator</TableCell>}
            <TableCell align="right">Schools</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={`${r.geo_level}-${r.area_code}-${r.breakdown_value}`} hover>
              <TableCell>
                {r.area_name}
                {r.geo_level === 'national' && (
                  <Chip label="National" size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                )}
              </TableCell>
              {breakdownLabel && <TableCell>{r.breakdown_label || r.breakdown_value}</TableCell>}
              <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatValue(r.value, unit, decimals)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(r.numerator)}</TableCell>
              {hasDenominator && (
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(r.denominator)}</TableCell>
              )}
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.schools.toLocaleString()}</TableCell>
              <TableCell>
                {r.status && (
                  <Chip label={r.status} size="small" color={STATUS_COLOR[r.status]} sx={{ height: 20, textTransform: 'capitalize' }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResultsTable;
